import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Claim } from '@wsphere/warranties/domain';
import { CLAIM_ITEM_CONTROLS_CONFIG } from '../../config/claim-item-controls.config';
import { ClaimItemHoldService, ClaimItemOwnerOption } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-item-escalation-dialog',
  templateUrl: './claim-item-escalation.dialog.html',
  standalone: false,
})
export class ClaimItemEscalationDialog {
  constructor(
    private formBuilder: FormBuilder,
    private holdSvc: ClaimItemHoldService,
    private toastSvc: ToastService
  ) {}

  readonly config = CLAIM_ITEM_CONTROLS_CONFIG;

  @ViewChild(DialogComponent) dialog?: DialogComponent;

  @Input() claim: Claim.Model | null = null;
  @Input() item: Claim.ClaimItem.Model | null = null;

  @Output() saved = new EventEmitter<string>();

  staff: ClaimItemOwnerOption[] = [];
  saving = false;

  form = this.formBuilder.group({
    reason: ['', [Validators.required, Validators.pattern(/\S/)]],
    ownerId: [null as string | null, Validators.required],
  });

  async openDialog() {
    this.form.reset({ reason: '', ownerId: null });
    this.dialog?.open();

    if (this.claim) {
      const response = await this.holdSvc.owners(this.claim.id).catch(() => null);
      // Escalations go to staff; service providers only own holds.
      this.staff = (response?.items ?? []).filter(owner => owner.ownerType === Claim.ClaimItem.Hold.OwnerType.STAFF);
    }
  }

  close() {
    this.dialog?.close();
  }

  async save() {
    if (!this.claim || !this.item || this.form.invalid || this.saving) return;

    this.saving = true;
    try {
      const result = await this.holdSvc
        .escalate(this.claim.id, this.item.id, {
          reason: (this.form.value.reason ?? '').trim(),
          ownerId: this.form.value.ownerId as string,
        })
        .catch((response: HttpErrorResponse) => {
          const message = response?.error?.message;
          this.toastSvc.show({
            type: 'error',
            title: this.config.escalationDialog.errorTitle,
            text: Array.isArray(message) ? message.join(' ') : message,
          });
          return null;
        });

      if (!result) return;

      this.toastSvc.show({ type: 'success', ...this.config.escalationDialog.saved });
      this.saved.emit(result.id);
      this.close();
    } finally {
      this.saving = false;
    }
  }
}
